import { gql, TypedDocumentNode } from "@apollo/client";

import { SEARCH_SUGGESTIONS_FRAGMENT } from "constants/suggestions";
import {
  Maybe,
  OpenTofuMigrationCounts,
  OpenTofuMigrationFailedIssue,
  OpenTofuMigrationQueueCleanResult,
  OpenTofuMigrationQueueEntry,
  OpenTofuMigrationWaitingForRunsIssue,
  PageInfo,
  QueryOpenTofuMigrateSearchArgs,
  QueryOpenTofuMigrateSuggestionsArgs,
  QueryOpenTofuMigrationQueueSearchArgs,
  QueryOpenTofuMigrationQueueSearchSuggestionsArgs,
  Run,
  SearchSuggestionsOutput,
  Stack,
  StackConfigVendor,
  StackConfigVendorTerraform,
} from "types/generated";

export type OpenTofuMigrationFailedIssueNode = Pick<
  OpenTofuMigrationFailedIssue,
  "__typename" | "note"
>;

export type OpenTofuMigrationWaitingForRunsIssueNode = Pick<
  OpenTofuMigrationWaitingForRunsIssue,
  "__typename" | "totalCount"
> & {
  runs: Array<Pick<Run, "id">>;
};

export type OpenTofuMigrationIssueNode =
  | OpenTofuMigrationFailedIssueNode
  | OpenTofuMigrationWaitingForRunsIssueNode;

export const OPEN_TOFU_MIGRATION_QUEUE_ENTRY_ISSUES_FRAGMENT = gql`
  fragment OpenTofuMigrationQueueEntryIssues on OpenTofuMigrationQueueEntry {
    issues {
      __typename
      ... on OpenTofuMigrationFailedIssue {
        note
      }
      ... on OpenTofuMigrationWaitingForRunsIssue {
        totalCount
        runs {
          id
        }
      }
    }
  }
`;

type FetchOpenTofuMigrationCountsData = {
  openTofuMigrationCounts: OpenTofuMigrationCounts;
};

export const GET_OPEN_TOFU_MIGRATION_COUNTS: TypedDocumentNode<FetchOpenTofuMigrationCountsData> = gql`
  query GetOpenTofuMigrationCounts {
    openTofuMigrationCounts {
      eligibleToMigrate
      queuedToMigrate
      queuedToRollback
      migrated
      rolledBack
      failed
    }
  }
`;

export type OpenTofuMigrateStackNode = Pick<Stack, "id" | "name"> & {
  vendorConfig: Maybe<
    Pick<StackConfigVendor, "__typename"> & Partial<Pick<StackConfigVendorTerraform, "version">>
  >;
};

type SearchOpenTofuMigrateStacksData = {
  openTofuMigrateSearch: {
    edges: Array<{ cursor: string; node: OpenTofuMigrateStackNode }>;
    pageInfo: PageInfo;
  };
};

export const SEARCH_OPEN_TOFU_MIGRATE_STACKS: TypedDocumentNode<
  SearchOpenTofuMigrateStacksData,
  Pick<QueryOpenTofuMigrateSearchArgs, "input">
> = gql`
  query SearchOpenTofuMigrateStacks($input: SearchInput!) {
    openTofuMigrateSearch(target: OpenTofu, input: $input) {
      edges {
        cursor
        node {
          id
          name
          vendorConfig {
            __typename
            ... on StackConfigVendorTerraform {
              version
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
    }
  }
`;

type SearchOpenTofuMigrateSuggestionsData = {
  openTofuMigrateSuggestions: SearchSuggestionsOutput;
};

export const SEARCH_OPEN_TOFU_MIGRATE_SUGGESTIONS: TypedDocumentNode<
  SearchOpenTofuMigrateSuggestionsData,
  Pick<QueryOpenTofuMigrateSuggestionsArgs, "input">
> = gql`
  query SearchOpenTofuMigrateSuggestions($input: SearchSuggestionsInput!) {
    openTofuMigrateSuggestions(target: OpenTofu, input: $input) {
      ...SearchSuggestionsFragment
    }
  }
  ${SEARCH_SUGGESTIONS_FRAGMENT}
`;

export type OpenTofuMigrationQueueEntryNode = Pick<
  OpenTofuMigrationQueueEntry,
  "id" | "state" | "updatedAt"
> & {
  issues: OpenTofuMigrationIssueNode[];
  stack: Pick<Stack, "id" | "name"> & {
    vendorConfig: Maybe<Pick<StackConfigVendor, "__typename">>;
  };
};

type SearchOpenTofuMigrationQueueData = {
  openTofuMigrationQueueSearch: {
    edges: Array<{ cursor: string; node: OpenTofuMigrationQueueEntryNode }>;
    pageInfo: PageInfo;
  };
};

export const SEARCH_OPEN_TOFU_MIGRATION_QUEUE: TypedDocumentNode<
  SearchOpenTofuMigrationQueueData,
  QueryOpenTofuMigrationQueueSearchArgs
> = gql`
  query SearchOpenTofuMigrationQueue($input: SearchInput!) {
    openTofuMigrationQueueSearch(input: $input) {
      edges {
        cursor
        node {
          id
          state
          updatedAt
          ...OpenTofuMigrationQueueEntryIssues
          stack {
            id
            name
            vendorConfig {
              __typename
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
    }
  }
  ${OPEN_TOFU_MIGRATION_QUEUE_ENTRY_ISSUES_FRAGMENT}
`;

type SearchOpenTofuMigrationQueueSuggestionsData = {
  openTofuMigrationQueueSearchSuggestions: SearchSuggestionsOutput;
};

export const SEARCH_OPEN_TOFU_MIGRATION_QUEUE_SUGGESTIONS: TypedDocumentNode<
  SearchOpenTofuMigrationQueueSuggestionsData,
  QueryOpenTofuMigrationQueueSearchSuggestionsArgs
> = gql`
  query SearchOpenTofuMigrationQueueSuggestions($input: SearchSuggestionsInput!) {
    openTofuMigrationQueueSearchSuggestions(input: $input) {
      ...SearchSuggestionsFragment
    }
  }
  ${SEARCH_SUGGESTIONS_FRAGMENT}
`;

type FetchOpenTofuMigrationQueueCleanData = {
  openTofuMigrationQueueClean: Pick<OpenTofuMigrationQueueCleanResult, "removed">;
};

export const OPEN_TOFU_MIGRATION_QUEUE_CLEAN: TypedDocumentNode<FetchOpenTofuMigrationQueueCleanData> = gql`
  mutation OpenTofuMigrationQueueClean {
    openTofuMigrationQueueClean {
      removed
    }
  }
`;
